import React, { useState, useEffect, useMemo } from 'react';
import { 
  BarChart3, 
  FileText, 
  List, 
  TrendingUp, 
  AlertTriangle, 
  AlertCircle, 
  CheckCircle, 
  Zap, 
  Clock, 
  Check, 
  X, 
  Paperclip, 
  Eye, 
  Edit, 
  Plus, 
  Search, 
  RefreshCw, 
  ArrowLeft, 
  Filter, 
  ArrowUpDown, 
  Users, 
  MapPin, 
  Tag, 
  FileStack, 
  Settings
} from 'lucide-react';
import { Decision } from '../../types/audit-trail';
import { useDecisions } from '../../hooks/useDecisions';
import { DecisionForm } from './DecisionForm';
import { cn, formatDate, formatRelativeTime, isMobile } from '../../lib/utils';
import { sampleDecisions, calculateDashboardStats } from '../../data/sampleDecisions';

interface AuditTrailProps {
  onBack?: () => void;
}

type ViewMode = 'list' | 'form' | 'detail' | 'quick' | 'timeline' | 'dashboard';
type FilterDateRange = 'all' | '24h' | '7d' | '30d' | 'custom';
type SortOption = 'date' | 'risk' | 'type' | 'status';

interface FilterState {
  searchTerm: string;
  riskLevel: string;
  type: string;
  status: string;
  dateRange: FilterDateRange;
  customStartDate?: string;
  customEndDate?: string;
  sortBy: SortOption;
  sortDirection: 'asc' | 'desc';
}

export const AuditTrail: React.FC<AuditTrailProps> = ({ onBack }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('dashboard');
  const [selectedDecision, setSelectedDecision] = useState<Decision | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [isSwipeView, setIsSwipeView] = useState(isMobile());
  
  const [filters, setFilters] = useState<FilterState>({
    searchTerm: '',
    riskLevel: '',
    type: '',
    status: '',
    dateRange: 'all',
    sortBy: 'date',
    sortDirection: 'desc'
  });

  const {
    decisions: hookDecisions,
    loading,
    error,
    syncStatus,
    pendingSyncCount,
    loadDecisions,
    triggerSync
  } = useDecisions();

  // Use sample data for demonstration, fallback to hook data
  const decisions = hookDecisions.length > 0 ? hookDecisions : sampleDecisions;

  useEffect(() => {
    loadDecisions();
  }, [loadDecisions]);

  // Handle window resize for mobile view
  useEffect(() => {
    const handleResize = () => {
      setIsSwipeView(isMobile());
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Enhanced filtering and sorting
  const filteredAndSortedDecisions = useMemo(() => {
    let filtered = decisions.filter(decision => {
      // Search filter
      const matchesSearch = !filters.searchTerm || 
        decision.title.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
        decision.decisionMade.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
        decision.rationale.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
        decision.stakeholders.some(s => s.toLowerCase().includes(filters.searchTerm.toLowerCase())) ||
        decision.tags.some(t => t.toLowerCase().includes(filters.searchTerm.toLowerCase()));
      
      // Risk level filter
      const matchesRisk = !filters.riskLevel || decision.riskLevel === filters.riskLevel;
      
      // Type filter
      const matchesType = !filters.type || decision.type === filters.type;
      
      // Status filter
      const matchesStatus = !filters.status || decision.status === filters.status;
      
      // Date range filter
      const matchesDate = (() => {
        if (filters.dateRange === 'all') return true;
        
        const decisionDate = new Date(decision.createdAt);
        const now = new Date();
        
        switch (filters.dateRange) {
          case '24h':
            return now.getTime() - decisionDate.getTime() <= 24 * 60 * 60 * 1000;
          case '7d':
            return now.getTime() - decisionDate.getTime() <= 7 * 24 * 60 * 60 * 1000;
          case '30d':
            return now.getTime() - decisionDate.getTime() <= 30 * 24 * 60 * 60 * 1000;
          case 'custom':
            if (filters.customStartDate && filters.customEndDate) {
              const startDate = new Date(filters.customStartDate);
              const endDate = new Date(filters.customEndDate);
              return decisionDate >= startDate && decisionDate <= endDate;
            }
            return true;
          default:
            return true;
        }
      })();
      
      return matchesSearch && matchesRisk && matchesType && matchesStatus && matchesDate;
    });
    
    // Sort decisions
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (filters.sortBy) {
        case 'date':
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
        case 'risk':
          const riskOrder = { low: 1, medium: 2, high: 3 };
          comparison = riskOrder[a.riskLevel] - riskOrder[b.riskLevel];
          break;
        case 'type':
          comparison = a.type.localeCompare(b.type);
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
      }
      
      return filters.sortDirection === 'desc' ? -comparison : comparison;
    });
    
    return filtered;
  }, [decisions, filters]);
  
  // Dashboard statistics
  const dashboardStats = useMemo(() => {
    return calculateDashboardStats(decisions);
  }, [decisions]);

  const handleNewDecision = () => {
    setSelectedDecision(null);
    setViewMode('form');
  };

  const handleQuickDecision = () => {
    setSelectedDecision(null);
    setViewMode('quick');
  };

  const handleEditDecision = (decision: Decision) => {
    setSelectedDecision(decision);
    setViewMode('form');
  };

  const handleViewDecision = (decision: Decision) => {
    setSelectedDecision(decision);
    setViewMode('detail');
  };

  const handleSaveDecision = (decision: Decision) => {
    setViewMode('list');
    setSelectedDecision(null);
    loadDecisions(); // Refresh the list
  };

  const handleCancel = () => {
    setViewMode('list');
    setSelectedDecision(null);
  };

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'high': return 'bg-red-50 text-red-700 border-red-200';
      case 'medium': return 'bg-orange-50 text-orange-700 border-orange-200';
      case 'low': return 'bg-green-50 text-green-700 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  
  const getRiskIcon = (risk: string) => {
    const className = "w-4 h-4";
    switch (risk) {
      case 'high': return <AlertTriangle className={cn(className, "text-red-600")} />;
      case 'medium': return <AlertCircle className={cn(className, "text-orange-600")} />;
      case 'low': return <CheckCircle className={cn(className, "text-green-600")} />;
      default: return <AlertCircle className={cn(className, "text-gray-600")} />;
    }
  };
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'bg-stone-100 text-stone-700';
      case 'submitted': return 'bg-blue-100 text-blue-800';
      case 'synced': return 'bg-teal-100 text-teal-800';
      case 'archived': return 'bg-slate-100 text-slate-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };
  
  const getSyncStatusIcon = (status: string) => {
    const className = "w-4 h-4";
    switch (status) {
      case 'synced': return <Check className={cn(className, "text-teal-600")} />;
      case 'pending': return <Clock className={cn(className, "text-orange-600")} />;
      case 'failed': return <X className={cn(className, "text-red-600")} />;
      default: return <Clock className={cn(className, "text-gray-600")} />;
    }
  };
  
  const updateFilter = (key: keyof FilterState, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };
  
  const clearFilters = () => {
    setFilters({
      searchTerm: '',
      riskLevel: '',
      type: '',
      status: '',
      dateRange: 'all',
      sortBy: 'date',
      sortDirection: 'desc'
    });
  };

  // Mobile Navigation Component
  const MobileNavigation = () => (
    <div className="mobile-nav md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50">
      <div className="grid grid-cols-4 gap-1 p-2">
        <button
          onClick={() => setViewMode('dashboard')}
          className={cn(
            "flex flex-col items-center py-2 px-1 rounded-lg text-xs",
            viewMode === 'dashboard' ? 'bg-blue-100 text-blue-700' : 'text-gray-600'
          )}
        >
          <BarChart3 className="w-5 h-5 mb-1" />
          <span>Dashboard</span>
        </button>
        <button
          onClick={() => setViewMode('list')}
          className={cn(
            "flex flex-col items-center py-2 px-1 rounded-lg text-xs",
            viewMode === 'list' ? 'bg-blue-100 text-blue-700' : 'text-gray-600'
          )}
        >
          <List className="w-5 h-5 mb-1" />
          <span>List</span>
        </button>
        <button
          onClick={() => setViewMode('timeline')}
          className={cn(
            "flex flex-col items-center py-2 px-1 rounded-lg text-xs",
            viewMode === 'timeline' ? 'bg-blue-100 text-blue-700' : 'text-gray-600'
          )}
        >
          <TrendingUp className="w-5 h-5 mb-1" />
          <span>Timeline</span>
        </button>
        <button
          onClick={handleNewDecision}
          className="flex flex-col items-center py-2 px-1 rounded-lg text-xs text-white hover:opacity-90 transition-opacity"
          style={{backgroundColor: '#454D66'}}
        >
          <Plus className="w-5 h-5 mb-1" />
          <span>New</span>
        </button>
      </div>
    </div>
  );

  // Dashboard View
  if (viewMode === 'dashboard') {
    return (
      <div className={cn("audit-trail min-h-screen", isSwipeView && "pb-20")}>
        <div className="dashboard-header p-4 text-white" style={{backgroundColor: '#454D66'}}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              {onBack && (
                <button onClick={onBack} className="p-2 rounded-lg bg-white/20 hover:bg-white/30">
                  <ArrowLeft className="w-4 h-4" />
                </button>
              )}
              <div>
                <h1 className="text-xl font-bold">Audit Dashboard</h1>
                <p className="text-sm opacity-90">Decision tracking overview</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={triggerSync} className="p-2 rounded-lg bg-white/20 hover:bg-white/30">
                <RefreshCw className="w-4 h-4" />
              </button>
              <button 
                onClick={handleQuickDecision} 
                className="flex items-center gap-1 px-3 py-1 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors"
              >
                <Zap className="w-4 h-4" /> Quick
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-white/20 rounded-lg p-3 backdrop-blur-sm border border-white/10">
              <div className="text-2xl font-bold">{dashboardStats.total}</div>
              <div className="text-sm opacity-90">Total Decisions</div>
            </div>
            <div className="bg-white/20 rounded-lg p-3 backdrop-blur-sm border border-white/10">
              <div className="text-2xl font-bold text-blue-200">{dashboardStats.recentActivity.last24h}</div>
              <div className="text-sm opacity-90">Last 24h</div>
            </div>
            <div className="bg-white/20 rounded-lg p-3 backdrop-blur-sm border border-white/10">
              <div className="text-2xl font-bold text-red-300">{dashboardStats.riskLevels.high}</div>
              <div className="text-sm opacity-90">High Risk</div>
            </div>
            <div className="bg-white/20 rounded-lg p-3 backdrop-blur-sm border border-white/10">
              <div className="text-2xl font-bold text-amber-300">{dashboardStats.recentActivity.pendingSync}</div>
              <div className="text-sm opacity-90">Pending Sync</div>
            </div>
          </div>
        </div>

        <div className="dashboard-content p-4 space-y-6">
          {/* Quick Actions */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <button 
              onClick={handleNewDecision}
              className="p-4 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-all duration-200 hover:shadow-md"
            >
              <div className="flex justify-center mb-2"><FileText className="w-8 h-8 text-blue-600" /></div>
              <div className="font-semibold text-blue-800">New Decision</div>
              <div className="text-sm text-blue-600">Document a new decision</div>
            </button>
            
            <button 
              onClick={() => setViewMode('list')}
              className="p-4 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 transition-all duration-200 hover:shadow-md"
            >
              <div className="flex justify-center mb-2"><List className="w-8 h-8 text-slate-600" /></div>
              <div className="font-semibold text-slate-800">View All</div>
              <div className="text-sm text-slate-600">Browse decision history</div>
            </button>
            
            <button 
              onClick={() => setViewMode('timeline')}
              className="p-4 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-all duration-200 hover:shadow-md"
            >
              <div className="flex justify-center mb-2"><TrendingUp className="w-8 h-8 text-purple-600" /></div>
              <div className="font-semibold text-purple-800">Timeline</div>
              <div className="text-sm text-purple-600">Visual decision timeline</div>
            </button>
          </div>

          {/* Recent Decisions */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Recent Decisions</h2>
            </div>
            <div className="divide-y divide-gray-200">
              {filteredAndSortedDecisions.slice(0, 5).map(decision => (
                <div 
                  key={decision.id} 
                  className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => handleViewDecision(decision)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={cn("flex items-center gap-1 px-2 py-1 rounded text-xs font-medium", getRiskColor(decision.riskLevel))}>
                          {getRiskIcon(decision.riskLevel)} {decision.riskLevel}
                        </span>
                        <span className={cn("px-2 py-1 rounded text-xs", getStatusColor(decision.status))}>
                          {decision.status}
                        </span>
                      </div>
                      <h3 className="font-medium text-gray-900 truncate">{decision.title}</h3>
                      <p className="text-sm text-gray-600 truncate">{decision.decisionMade}</p>
                      <div className="text-xs text-gray-500 mt-1">
                        {formatRelativeTime(decision.createdAt)} • {decision.createdByName}
                      </div>
                    </div>
                    <div className="ml-4 flex-shrink-0">
                      <button className="text-gray-400 hover:text-gray-600 p-1">
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {decisions.length === 0 && !loading && (
              <div className="p-12 text-center bg-gray-50 rounded-lg">
                <div className="max-w-md mx-auto">
                  <div className="flex justify-center mb-6">
                    <FileStack className="w-16 h-16 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Ready to document your first decision?</h3>
                  <p className="text-gray-600 mb-8 leading-relaxed">Create a comprehensive audit trail for humanitarian decision-making. Every decision matters, and accountability starts here.</p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <button 
                      onClick={handleNewDecision} 
                      className="flex items-center justify-center gap-2 px-6 py-3 text-white rounded-lg hover:opacity-90 transition-all duration-200 hover:shadow-lg font-medium" 
                      style={{backgroundColor: '#454D66'}}
                    >
                      <FileText className="w-5 h-5" /> Start Documentation
                    </button>
                    <button 
                      onClick={handleQuickDecision} 
                      className="flex items-center justify-center gap-2 px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all duration-200 hover:shadow-lg font-medium"
                    >
                      <Zap className="w-5 h-5" /> Emergency Decision
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Enhanced Risk Distribution & Analytics */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Risk Distribution Chart */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Risk Distribution</h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-100">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full" style={{backgroundColor: '#E57373'}}></div>
                    <span className="font-medium" style={{color: '#C62828'}}>High Risk</span>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold" style={{color: '#D32F2F'}}>{dashboardStats.riskLevels.high}</div>
                    <div className="text-xs" style={{color: '#E57373'}}>Immediate attention required</div>
                  </div>
                </div>
                <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg border border-orange-100">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full" style={{backgroundColor: '#FF8A65'}}></div>
                    <span className="font-medium" style={{color: '#E64A19'}}>Medium Risk</span>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold" style={{color: '#FF5722'}}>{dashboardStats.riskLevels.medium}</div>
                    <div className="text-xs" style={{color: '#FF8A65'}}>Monitor closely</div>
                  </div>
                </div>
                <div className="flex items-center justify-between p-4 rounded-lg border" style={{backgroundColor: '#E8F5E8', borderColor: '#C8E6C9'}}>
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full" style={{backgroundColor: '#58B368'}}></div>
                    <span className="font-medium" style={{color: '#2E7D32'}}>Low Risk</span>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold" style={{color: '#58B368'}}>{dashboardStats.riskLevels.low}</div>
                    <div className="text-xs" style={{color: '#81C784'}}>Standard monitoring</div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Decision Types Distribution */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Decision Types</h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-sm text-gray-600">
                    <Settings className="w-4 h-4" /> Security Response
                  </span>
                  <span className="text-sm font-medium text-gray-900">{dashboardStats.types.security_response}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-sm text-gray-600">
                    <FileStack className="w-4 h-4" /> Resource Allocation
                  </span>
                  <span className="text-sm font-medium text-gray-900">{dashboardStats.types.resource_allocation}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-sm text-gray-600">
                    <Users className="w-4 h-4" /> Beneficiary Selection
                  </span>
                  <span className="text-sm font-medium text-gray-900">{dashboardStats.types.beneficiary_selection}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-sm text-gray-600">
                    <BarChart3 className="w-4 h-4" /> Program Modification
                  </span>
                  <span className="text-sm font-medium text-gray-900">{dashboardStats.types.program_modification}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-sm text-gray-600">
                    <Users className="w-4 h-4" /> Partner Management
                  </span>
                  <span className="text-sm font-medium text-gray-900">{dashboardStats.types.partner_management}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-sm text-gray-600">
                    <FileText className="w-4 h-4" /> Other
                  </span>
                  <span className="text-sm font-medium text-gray-900">{dashboardStats.types.other}</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Quick Analytics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-lg p-4 text-white" style={{backgroundColor: '#454D66'}}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm opacity-90">This Week</div>
                  <div className="text-2xl font-bold">{dashboardStats.recentActivity.last7days}</div>
                  <div className="text-sm opacity-75">New decisions</div>
                </div>
                <TrendingUp className="w-8 h-8 opacity-75" />
              </div>
            </div>
            
            <div className="rounded-lg p-4 text-white bg-red-600">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm opacity-90">Urgent Actions</div>
                  <div className="text-2xl font-bold">{dashboardStats.recentActivity.highRiskUrgent}</div>
                  <div className="text-sm opacity-75">High risk + urgent</div>
                </div>
                <Zap className="w-8 h-8 opacity-75" />
              </div>
            </div>
            
            <div className="rounded-lg p-4 text-white" style={{backgroundColor: '#009975'}}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm opacity-90">System Health</div>
                  <div className="text-2xl font-bold">{Math.round((dashboardStats.total - dashboardStats.recentActivity.pendingSync) / Math.max(dashboardStats.total, 1) * 100)}%</div>
                  <div className="text-sm opacity-75">Sync rate</div>
                </div>
                <CheckCircle className="w-8 h-8 opacity-75" />
              </div>
            </div>
          </div>
        </div>
        
        {isSwipeView && <MobileNavigation />}
      </div>
    );
  }

  // Timeline View
  if (viewMode === 'timeline') {
    return (
      <div className={cn("audit-trail min-h-screen", isSwipeView && "pb-20")}>
        <div className="timeline-header p-4 bg-white border-b border-gray-200 sticky top-0 z-40">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => setViewMode('dashboard')} className="p-2 rounded-lg hover:bg-gray-100">
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Decision Timeline</h1>
                <p className="text-sm text-gray-600">Chronological view of decisions</p>
              </div>
            </div>
            <button 
              onClick={() => setShowFilters(!showFilters)}
              className="p-2 rounded-lg hover:bg-gray-100"
            >
              <Search className="w-4 h-4" />
            </button>
          </div>
          
          {showFilters && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                <select
                  value={filters.dateRange}
                  onChange={(e) => updateFilter('dateRange', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="all">All Time</option>
                  <option value="24h">Last 24 Hours</option>
                  <option value="7d">Last 7 Days</option>
                  <option value="30d">Last 30 Days</option>
                </select>
                
                <select
                  value={filters.riskLevel}
                  onChange={(e) => updateFilter('riskLevel', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">All Risk Levels</option>
                  <option value="high">High Risk</option>
                  <option value="medium">Medium Risk</option>
                  <option value="low">Low Risk</option>
                </select>
                
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="timeline-content p-4">
          {loading && (
            <div className="flex justify-center items-center h-32">
              <div className="flex items-center gap-2 text-xl">
                <Clock className="w-6 h-6 animate-spin" /> Loading timeline...
              </div>
            </div>
          )}
          
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-6 top-0 bottom-0 w-px bg-gray-200"></div>
            
            <div className="space-y-6">
              {filteredAndSortedDecisions.map((decision, index) => (
                <div key={decision.id} className="relative flex items-start">
                  {/* Timeline dot */}
                  <div className={cn(
                    "absolute left-4 w-4 h-4 rounded-full border-2 bg-white z-10",
                    decision.riskLevel === 'high' ? 'border-red-500' : 
                    decision.riskLevel === 'medium' ? 'border-orange-500' : 'border-green-500'
                  )}></div>
                  
                  {/* Decision card */}
                  <div className="ml-12 bg-white rounded-lg border border-gray-200 p-4 w-full hover:shadow-lg transition-shadow cursor-pointer"
                       onClick={() => handleViewDecision(decision)}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={cn("flex items-center gap-1 px-2 py-1 rounded text-xs font-medium", getRiskColor(decision.riskLevel))}>
                          {getRiskIcon(decision.riskLevel)} {decision.riskLevel}
                        </span>
                        <span className={cn("px-2 py-1 rounded text-xs", getStatusColor(decision.status))}>
                          {decision.status}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500">{formatRelativeTime(decision.createdAt)}</span>
                    </div>
                    
                    <h3 className="font-semibold text-gray-900 mb-1">{decision.title}</h3>
                    <p className="text-sm text-gray-600 mb-2 line-clamp-2">{decision.decisionMade}</p>
                    
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>By {decision.createdByName}</span>
                      <div className="flex items-center gap-4">
                        {decision.evidence?.length > 0 && (
                          <span className="flex items-center gap-1">
                            <Paperclip className="w-3 h-3" /> {decision.evidence.length} files
                          </span>
                        )}
                        <span>{decision.informationSources.length} sources</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {filteredAndSortedDecisions.length === 0 && (
              <div className="text-center py-12">
                <div className="flex justify-center mb-4">
                  <TrendingUp className="w-16 h-16 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No decisions found</h3>
                <p className="text-gray-600">Try adjusting your filters or create a new decision</p>
              </div>
            )}
          </div>
        </div>
        
        {isSwipeView && <MobileNavigation />}
      </div>
    );
  }

  // Enhanced List View
  if (viewMode === 'list') {
    return (
      <div className={cn("audit-trail min-h-screen", isSwipeView && "pb-20")}>
        <div className="audit-trail-header p-4 bg-white border-b border-gray-200 sticky top-0 z-40">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <button onClick={() => setViewMode('dashboard')} className="p-2 rounded-lg hover:bg-gray-100">
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Decision Audit Trail</h1>
                <p className="text-sm text-gray-600">Document and protect your humanitarian decisions</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={handleQuickDecision} className="flex items-center gap-1 px-3 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm">
                <Zap className="w-4 h-4" /> Quick
              </button>
              <button onClick={handleNewDecision} className="flex items-center gap-1 px-3 py-1 text-white rounded-lg hover:opacity-90 text-sm" style={{backgroundColor: '#454D66'}}>
                <Plus className="w-4 h-4" /> New
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              {pendingSyncCount > 0 && (
                <span className="flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs font-medium">
                  <Clock className="w-3 h-3" /> {pendingSyncCount} pending sync
                </span>
              )}
              <span className={cn("flex items-center gap-1 px-2 py-1 rounded text-xs font-medium",
                syncStatus === 'synced' ? 'bg-teal-100 text-teal-700' :
                syncStatus === 'pending' ? 'bg-orange-100 text-orange-700' :
                'bg-red-100 text-red-700'
              )}>
                {syncStatus === 'synced' && <><Check className="w-3 h-3" /> All synced</>}
                {syncStatus === 'pending' && <><Clock className="w-3 h-3" /> Syncing...</>}
                {syncStatus === 'failed' && <><X className="w-3 h-3" /> Sync failed</>}
              </span>
            </div>
            <button 
              onClick={triggerSync} 
              disabled={loading}
              className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:bg-gray-400 disabled:text-gray-100"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-3">
            <div className="relative">
              <input
                type="text"
                placeholder="Search decisions..."
                value={filters.searchTerm}
                onChange={(e) => updateFilter('searchTerm', e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
              <div className="absolute left-3 top-2.5 text-gray-400"><Search className="w-4 h-4" /></div>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={cn(
                  "flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                  showFilters ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                )}
              >
                <Filter className="w-4 h-4" /> Filters
              </button>
              <select
                value={filters.sortBy}
                onChange={(e) => updateFilter('sortBy', e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="date">Sort by Date</option>
                <option value="risk">Sort by Risk</option>
                <option value="type">Sort by Type</option>
                <option value="status">Sort by Status</option>
              </select>
              <button
                onClick={() => updateFilter('sortDirection', filters.sortDirection === 'desc' ? 'asc' : 'desc')}
                className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                <ArrowUpDown className="w-4 h-4" />
              </button>
            </div>
            
            {showFilters && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 p-4 bg-gray-50 rounded-lg">
                <select
                  value={filters.riskLevel}
                  onChange={(e) => updateFilter('riskLevel', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">All Risk Levels</option>
                  <option value="high">High Risk</option>
                  <option value="medium">Medium Risk</option>
                  <option value="low">Low Risk</option>
                </select>

                <select
                  value={filters.type}
                  onChange={(e) => updateFilter('type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">All Types</option>
                  <option value="security_response">Security Response</option>
                  <option value="resource_allocation">Resource Allocation</option>
                  <option value="beneficiary_selection">Beneficiary Selection</option>
                  <option value="program_modification">Program Modification</option>
                  <option value="partner_management">Partner Management</option>
                  <option value="other">Other</option>
                </select>
                
                <select
                  value={filters.status}
                  onChange={(e) => updateFilter('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">All Statuses</option>
                  <option value="draft">Draft</option>
                  <option value="submitted">Submitted</option>
                  <option value="approved">Approved</option>
                  <option value="archived">Archived</option>
                </select>
                
                <select
                  value={filters.dateRange}
                  onChange={(e) => updateFilter('dateRange', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="all">All Time</option>
                  <option value="24h">Last 24 Hours</option>
                  <option value="7d">Last 7 Days</option>
                  <option value="30d">Last 30 Days</option>
                </select>
                
                <div className="sm:col-span-2 lg:col-span-4 flex justify-end">
                  <button
                    onClick={clearFilters}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    Clear All Filters
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="decisions-container p-4">
          {loading && decisions.length === 0 && (
            <div className="flex justify-center items-center h-32">
              <div className="text-center">
                <div className="flex justify-center mb-2">
                  <Clock className="w-8 h-8 text-gray-400 animate-spin" />
                </div>
                <p className="text-gray-600">Loading your decisions...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-coral-50 border border-coral-200 rounded-lg p-4 text-center">
              <div className="flex items-center gap-2 text-coral-700 mb-3">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                </svg>
                <span>Error: {error}</span>
              </div>
              <button 
                onClick={() => loadDecisions()} 
                className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                <RefreshCw className="w-4 h-4" /> Retry
              </button>
            </div>
          )}

          {!loading && filteredAndSortedDecisions.length === 0 && filters.searchTerm && (
            <div className="text-center py-12">
              <div className="flex justify-center mb-4">
                <Search className="w-16 h-16 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No decisions found</h3>
              <p className="text-gray-600 mb-4">No decisions match "{filters.searchTerm}"</p>
              <button 
                onClick={() => updateFilter('searchTerm', '')} 
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Clear search
              </button>
            </div>
          )}

          {!loading && decisions.length === 0 && !filters.searchTerm && (
            <div className="text-center py-12">
              <div className="flex justify-center mb-4">
                <FileStack className="w-16 h-16 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No decisions documented yet</h3>
              <p className="text-gray-600 mb-6">Start building your audit trail by documenting your first decision</p>
              <div className="space-y-2">
                <button 
                  onClick={handleNewDecision} 
                  className="flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 text-white rounded-lg hover:opacity-90 font-medium" 
                  style={{backgroundColor: '#454D66'}}
                >
                  <FileText className="w-5 h-5" /> Document First Decision
                </button>
                <button 
                  onClick={handleQuickDecision} 
                  className="flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 font-medium ml-0 sm:ml-2"
                >
                  <Zap className="w-5 h-5" /> Quick Emergency Decision
                </button>
              </div>
            </div>
          )}

          {filteredAndSortedDecisions.length > 0 && (
            <div className="space-y-4">
              {isSwipeView ? (
                // Mobile card layout
                filteredAndSortedDecisions.map(decision => (
                  <div 
                    key={decision.id} 
                    className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-lg transition-all duration-200"
                    onClick={() => handleViewDecision(decision)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={cn("flex items-center gap-1 px-2 py-1 rounded text-xs font-medium", getRiskColor(decision.riskLevel))}>
                          {getRiskIcon(decision.riskLevel)} {decision.riskLevel}
                        </span>
                        <span className={cn("px-2 py-1 rounded text-xs", getStatusColor(decision.status))}>
                          {decision.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 ml-2">
                        <button 
                          onClick={(e) => { e.stopPropagation(); handleEditDecision(decision); }}
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    <h3 className="font-semibold text-gray-900 mb-2 leading-tight">{decision.title}</h3>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{decision.decisionMade}</p>
                    
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                      <span>{formatRelativeTime(decision.createdAt)}</span>
                      <span>By {decision.createdByName}</span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        {decision.evidence && decision.evidence.length > 0 && (
                          <span className="flex items-center gap-1">
                            <Paperclip className="w-3 h-3" /> {decision.evidence.length}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <BarChart3 className="w-3 h-3" /> {decision.informationSources.length} sources
                        </span>
                        <span className={cn("flex items-center gap-1 px-2 py-1 rounded text-xs",
                          decision.syncStatus === 'synced' ? 'bg-teal-100 text-teal-700' :
                          decision.syncStatus === 'pending' ? 'bg-orange-100 text-orange-700' :
                          'bg-red-100 text-red-700'
                        )}>
                          {getSyncStatusIcon(decision.syncStatus)}
                        </span>
                      </div>
                    </div>
                    
                    {decision.tags && decision.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-3">
                        {decision.tags.slice(0, 2).map(tag => (
                          <span key={tag} className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                            {tag}
                          </span>
                        ))}
                        {decision.tags.length > 2 && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                            +{decision.tags.length - 2}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                // Desktop grid layout
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                  {filteredAndSortedDecisions.map(decision => (
                    <div 
                      key={decision.id} 
                      className="bg-white rounded-lg border border-gray-200 hover:shadow-lg transition-all duration-200 overflow-hidden"
                    >
                      <div className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={cn("px-2 py-1 rounded text-xs font-medium", getRiskColor(decision.riskLevel))}>
                              {getRiskIcon(decision.riskLevel)} {decision.riskLevel}
                            </span>
                            <span className={cn("px-2 py-1 rounded text-xs", getStatusColor(decision.status))}>
                              {decision.status}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <button 
                              onClick={() => handleViewDecision(decision)}
                              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                              title="View details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button 
                              onClick={() => handleEditDecision(decision)}
                              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                              title="Edit decision"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        
                        <h3 className="font-semibold text-gray-900 mb-2 leading-tight">{decision.title}</h3>
                        <p className="text-sm text-gray-600 mb-3 line-clamp-3">{decision.decisionMade}</p>
                        
                        <div className="space-y-2 text-xs text-gray-500">
                          <div className="flex items-center justify-between">
                            <span>Created: {formatDate(decision.createdAt)}</span>
                            <span className={cn("flex items-center gap-1 px-2 py-1 rounded text-xs",
                              decision.syncStatus === 'synced' ? 'bg-teal-100 text-teal-700' :
                              decision.syncStatus === 'pending' ? 'bg-orange-100 text-orange-700' :
                              'bg-red-100 text-red-700'
                            )}>
                              {getSyncStatusIcon(decision.syncStatus)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span>By: {decision.createdByName}</span>
                            <div className="flex items-center gap-3">
                              {decision.evidence && decision.evidence.length > 0 && (
                                <span className="flex items-center gap-1">
                                  <Paperclip className="w-3 h-3" /> {decision.evidence.length} files
                                </span>
                              )}
                              <span className="flex items-center gap-1">
                                <BarChart3 className="w-3 h-3" /> {decision.informationSources.length} sources
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        {decision.tags && decision.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-3">
                            {decision.tags.slice(0, 3).map(tag => (
                              <span key={tag} className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                                {tag}
                              </span>
                            ))}
                            {decision.tags.length > 3 && (
                              <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                                +{decision.tags.length - 3}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        
        {isSwipeView && <MobileNavigation />}
      </div>
    );
  }

  // Form Views
  if (viewMode === 'form' || viewMode === 'quick') {
    return (
      <div className={cn("audit-trail min-h-screen", isSwipeView && "pb-20")}>
        <div className="form-container">
          <DecisionForm
            initialDecision={selectedDecision || undefined}
            onSave={handleSaveDecision}
            onCancel={handleCancel}
            quickMode={viewMode === 'quick'}
            className={isSwipeView ? "mobile-optimized" : ""}
          />
        </div>
        {isSwipeView && <MobileNavigation />}
      </div>
    );
  }

  // Enhanced Detail View
  if (viewMode === 'detail' && selectedDecision) {
    return (
      <div className={cn("audit-trail min-h-screen", isSwipeView && "pb-20")}>
        <div className={cn("detail-container", isSwipeView ? "mobile-detail" : "desktop-detail")}>
          <div className="detail-header p-4 bg-white border-b border-gray-200 sticky top-0 z-40">
            <div className="flex items-center justify-between">
              <button 
                onClick={handleCancel} 
                className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => handleEditDecision(selectedDecision)} 
                  className="flex items-center gap-2 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-colors" 
                  style={{backgroundColor: '#454D66'}}
                >
                  <Edit className="w-4 h-4" /> Edit
                </button>
              </div>
            </div>
          </div>

          <div className="decision-detail p-4">
            {isSwipeView ? (
              // Mobile layout - single column
              <div className="space-y-6">
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <h1 className="text-xl font-bold text-gray-900 mb-4">{selectedDecision.title}</h1>
                  
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    <div>
                      <span className="text-sm text-gray-500">Type</span>
                      <div className="font-medium text-gray-900">{selectedDecision.type.replace('_', ' ')}</div>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Risk Level</span>
                      <div className={cn("inline-flex px-2 py-1 rounded text-sm font-medium", getRiskColor(selectedDecision.riskLevel))}>
                        {getRiskIcon(selectedDecision.riskLevel)} {selectedDecision.riskLevel}
                      </div>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Created</span>
                      <div className="font-medium text-gray-900">{formatDate(selectedDecision.createdAt)}</div>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">By</span>
                      <div className="font-medium text-gray-900">{selectedDecision.createdByName}</div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-3">
                    <FileText className="w-5 h-5" /> Decision Made
                  </h3>
                  <p className="text-gray-700 leading-relaxed">{selectedDecision.decisionMade}</p>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-3">
                    <AlertCircle className="w-5 h-5" /> Rationale
                  </h3>
                  <p className="text-gray-700 leading-relaxed">{selectedDecision.rationale}</p>
                </div>

                {selectedDecision.alternatives && (
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-3">
                      <ArrowUpDown className="w-5 h-5" /> Alternatives Considered
                    </h3>
                    <p className="text-gray-700 leading-relaxed">{selectedDecision.alternatives}</p>
                  </div>
                )}

                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-3">
                    <BarChart3 className="w-5 h-5" /> Information Sources
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedDecision.informationSources.map(source => (
                      <span key={source} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                        {source}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-3">
                    <Users className="w-5 h-5" /> Stakeholders Consulted
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedDecision.stakeholders.map(stakeholder => (
                      <span key={stakeholder} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                        {stakeholder}
                      </span>
                    ))}
                  </div>
                </div>

                {selectedDecision.location && (
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-3">
                      <MapPin className="w-5 h-5" /> Location
                    </h3>
                    <p className="text-gray-700">
                      Coordinates: {selectedDecision.location.lat.toFixed(6)}, {selectedDecision.location.lng.toFixed(6)}
                    </p>
                    {selectedDecision.location.address && (
                      <p className="text-gray-600 mt-1">{selectedDecision.location.address}</p>
                    )}
                  </div>
                )}

                {selectedDecision.tags && selectedDecision.tags.length > 0 && (
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-3">
                      <Tag className="w-5 h-5" /> Tags
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedDecision.tags.map(tag => (
                        <span key={tag} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Audit Information */}
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-3">
                    <Settings className="w-5 h-5" /> Audit Information
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Status</span>
                      <span className={cn("px-2 py-1 rounded text-sm", getStatusColor(selectedDecision.status))}>
                        {selectedDecision.status}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Sync Status</span>
                      <span className={cn("flex items-center gap-1 px-2 py-1 rounded text-sm",
                        selectedDecision.syncStatus === 'synced' ? 'bg-teal-100 text-teal-700' :
                        selectedDecision.syncStatus === 'pending' ? 'bg-orange-100 text-orange-700' :
                        'bg-red-100 text-red-700'
                      )}>
                        {getSyncStatusIcon(selectedDecision.syncStatus)}
                        {selectedDecision.syncStatus}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Last Updated</span>
                      <span className="text-sm text-gray-700">{formatDate(selectedDecision.updatedAt)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Digital Signature</span>
                      <span className="text-xs text-gray-600 font-mono">{selectedDecision.signature.slice(0, 16)}...</span>
                    </div>
                  </div>
                </div>

                {/* Evidence */}
                {selectedDecision.evidence && selectedDecision.evidence.length > 0 && (
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-3">
                      <Paperclip className="w-5 h-5" /> Evidence ({selectedDecision.evidence.length})
                    </h3>
                    <div className="space-y-3">
                      {selectedDecision.evidence.map(evidence => (
                        <div key={evidence.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 truncate">{evidence.filename}</div>
                            <div className="text-sm text-gray-500">
                              {(evidence.size / 1024).toFixed(1)} KB
                            </div>
                          </div>
                          <span className={cn("px-2 py-1 rounded text-xs",
                            evidence.uploadStatus === 'complete' ? 'bg-sage-100 text-sage-700' :
                            evidence.uploadStatus === 'pending' ? 'bg-terracotta-100 text-terracotta-700' :
                            'bg-coral-100 text-coral-700'
                          )}>
                            {evidence.uploadStatus === 'complete' && <div className="w-2 h-2 bg-sage-500 rounded-full"></div>}
                            {evidence.uploadStatus === 'pending' && <div className="w-2 h-2 bg-terracotta-500 rounded"></div>}
                            {evidence.uploadStatus === 'failed' && <div className="w-2 h-2 bg-coral-500 rounded-sm"></div>}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              // Desktop layout - two columns
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h1 className="text-2xl font-bold text-gray-900 mb-4">{selectedDecision.title}</h1>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div>
                        <span className="text-sm text-gray-500">Type</span>
                        <div className="font-medium text-gray-900">{selectedDecision.type.replace('_', ' ')}</div>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">Risk Level</span>
                        <div className={cn("inline-flex px-2 py-1 rounded text-sm font-medium", getRiskColor(selectedDecision.riskLevel))}>
                          {getRiskIcon(selectedDecision.riskLevel)} {selectedDecision.riskLevel}
                        </div>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">Created</span>
                        <div className="font-medium text-gray-900">{formatDate(selectedDecision.createdAt)}</div>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">By</span>
                        <div className="font-medium text-gray-900">{selectedDecision.createdByName}</div>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div>
                        <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-3">
                          <FileText className="w-5 h-5" /> Decision Made
                        </h3>
                        <p className="text-gray-700 leading-relaxed">{selectedDecision.decisionMade}</p>
                      </div>

                      <div>
                        <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-3">
                          <AlertCircle className="w-5 h-5" /> Rationale
                        </h3>
                        <p className="text-gray-700 leading-relaxed">{selectedDecision.rationale}</p>
                      </div>

                      {selectedDecision.alternatives && (
                        <div>
                          <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-3">
                            <ArrowUpDown className="w-5 h-5" /> Alternatives Considered
                          </h3>
                          <p className="text-gray-700 leading-relaxed">{selectedDecision.alternatives}</p>
                        </div>
                      )}

                      <div>
                        <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-3">
                          <BarChart3 className="w-5 h-5" /> Information Sources
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {selectedDecision.informationSources.map(source => (
                            <span key={source} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                              {source}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-3">
                          <Users className="w-5 h-5" /> Stakeholders Consulted
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {selectedDecision.stakeholders.map(stakeholder => (
                            <span key={stakeholder} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                              {stakeholder}
                            </span>
                          ))}
                        </div>
                      </div>

                      {selectedDecision.location && (
                        <div>
                          <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-3">
                            <MapPin className="w-5 h-5" /> Location
                          </h3>
                          <p className="text-gray-700">
                            Coordinates: {selectedDecision.location.lat.toFixed(6)}, {selectedDecision.location.lng.toFixed(6)}
                          </p>
                          {selectedDecision.location.address && (
                            <p className="text-gray-600 mt-1">{selectedDecision.location.address}</p>
                          )}
                        </div>
                      )}

                      {selectedDecision.tags && selectedDecision.tags.length > 0 && (
                        <div>
                          <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-3">
                            <Tag className="w-5 h-5" /> Tags
                          </h3>
                          <div className="flex flex-wrap gap-2">
                            {selectedDecision.tags.map(tag => (
                              <span key={tag} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Desktop Sidebar */}
                <div className="space-y-6">
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-3">
                      <Settings className="w-5 h-5" /> Audit Information
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">Status</span>
                        <span className={cn("px-2 py-1 rounded text-sm", getStatusColor(selectedDecision.status))}>
                          {selectedDecision.status}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">Sync Status</span>
                        <span className={cn("flex items-center gap-1 px-2 py-1 rounded text-sm",
                          selectedDecision.syncStatus === 'synced' ? 'bg-green-100 text-green-700' :
                          selectedDecision.syncStatus === 'pending' ? 'bg-orange-100 text-orange-700' :
                          'bg-red-100 text-red-700'
                        )}>
                          {getSyncStatusIcon(selectedDecision.syncStatus)}
                          {selectedDecision.syncStatus}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">Last Updated</span>
                        <span className="text-sm text-gray-700">{formatDate(selectedDecision.updatedAt)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">Digital Signature</span>
                        <span className="text-xs text-gray-600 font-mono">{selectedDecision.signature.slice(0, 16)}...</span>
                      </div>
                    </div>
                  </div>

                  {selectedDecision.evidence && selectedDecision.evidence.length > 0 && (
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-3">
                      <Paperclip className="w-5 h-5" /> Evidence ({selectedDecision.evidence.length})
                    </h3>
                      <div className="space-y-3">
                        {selectedDecision.evidence.map(evidence => (
                          <div key={evidence.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-900 truncate">{evidence.filename}</div>
                              <div className="text-sm text-gray-500">
                                {(evidence.size / 1024).toFixed(1)} KB
                              </div>
                            </div>
                            <span className={cn("flex items-center gap-1 px-2 py-1 rounded text-xs",
                              evidence.uploadStatus === 'complete' ? 'bg-green-100 text-green-700' :
                              evidence.uploadStatus === 'pending' ? 'bg-orange-100 text-orange-700' :
                              'bg-red-100 text-red-700'
                            )}>
                              {evidence.uploadStatus === 'complete' && <Check className="w-3 h-3" />}
                              {evidence.uploadStatus === 'pending' && <Clock className="w-3 h-3" />}
                              {evidence.uploadStatus === 'failed' && <X className="w-3 h-3" />}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        {isSwipeView && <MobileNavigation />}
      </div>
    );
  }

  return null;
};

export default AuditTrail;